import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, TeamAdmin, ContainerSecuritySettings } from '@byscott-io/corebyscott';
import { account } from '../api/client';

export default function AdminPage() {
  const { currentAccount } = useAuth();
  const [activeTab, setActiveTab] = useState('team');
  const isAdmin = currentAccount?.role === 'owner' || currentAccount?.role === 'admin';

  const tabs = [
    { id: 'team', label: 'Team', adminOnly: false },
    { id: 'security', label: 'Security', adminOnly: true },
    { id: 'square', label: 'Square', adminOnly: true },
  ].filter(tab => !tab.adminOnly || isAdmin);

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to="/" className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; Back to Dashboard
        </Link>
        <h1 className="text-2xl font-bold mt-2">Admin Settings</h1>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-6 text-sm font-medium border-b-2 cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'team' && <TeamAdmin />}
          {activeTab === 'security' && <ContainerSecuritySettings />}
          {activeTab === 'square' && <SquareConfigAdmin />}
        </div>
      </div>
    </div>
  );
}

function SquareConfigAdmin() {
  const [squareConfig, setSquareConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [environment, setEnvironment] = useState('sandbox');
  const [applicationId, setApplicationId] = useState('');
  const [accessToken, setAccessToken] = useState('');

  const loadSquareConfig = async () => {
    try {
      setLoading(true);
      const response = await account.getSquareConfig();
      setSquareConfig(response.data);
      if (response.data?.environment) {
        setEnvironment(response.data.environment);
      }
    } catch {
      setError('Failed to load Square configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSquareConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!applicationId || !accessToken) {
      setError('Application ID and Access Token are required');
      return;
    }

    try {
      setSaving(true);
      await account.updateSquareConfig({
        environment,
        application_id: applicationId,
        access_token: accessToken,
      });
      setSuccess('Square configuration saved successfully');
      setApplicationId('');
      setAccessToken('');
      setShowForm(false);
      loadSquareConfig();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save Square configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!confirm('Are you sure you want to clear the Square configuration? Payment processing will be disabled.')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      setSaving(true);
      await account.updateSquareConfig({ clear: true });
      setSuccess('Square configuration cleared');
      loadSquareConfig();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear Square configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500">Loading Square configuration...</div>;
  }

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-4">Square Configuration</h2>

      <p className="text-sm text-gray-500 mb-6">
        Configure your Square account credentials here. Square is used as a payment gateway for
        storing customer cards and processing payments. All subscription management is handled internally.
      </p>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4 mb-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Current Status */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-900">Current Status</h3>
          <span className={`px-2 py-1 text-xs rounded-full ${
            squareConfig?.configured
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {squareConfig?.configured ? 'Configured' : 'Not Configured'}
          </span>
        </div>

        {squareConfig?.configured && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Environment:</span>
              <span className={`font-mono px-2 py-0.5 rounded ${
                squareConfig.environment === 'production'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {squareConfig.environment}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Application ID:</span>
              <span className="font-mono text-gray-700">
                {squareConfig.application_id || 'Not set'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Access Token:</span>
              <span className="font-mono text-gray-700">
                {squareConfig.has_access_token ? '••••••••••••••••' : 'Not set'}
              </span>
            </div>
          </div>
        )}

        {!squareConfig?.configured && (
          <p className="text-sm text-gray-500">
            Square is not configured. Payment processing will not be available.
          </p>
        )}
      </div>

      {/* Configuration Form */}
      {showForm ? (
        <form onSubmit={handleSave} className="max-w-md space-y-4">
          <div>
            <label htmlFor="environment" className="block text-sm font-medium text-gray-700">
              Environment *
            </label>
            <select
              id="environment"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="sandbox">Sandbox (Testing)</option>
              <option value="production">Production</option>
            </select>
          </div>

          <div>
            <label htmlFor="applicationId" className="block text-sm font-medium text-gray-700">
              Application ID *
            </label>
            <input
              type="text"
              id="applicationId"
              value={applicationId}
              onChange={(e) => setApplicationId(e.target.value)}
              placeholder="sandbox-sq0idb-..."
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              From Square Developer Dashboard → Applications → Credentials
            </p>
          </div>

          <div>
            <label htmlFor="accessToken" className="block text-sm font-medium text-gray-700">
              Access Token *
            </label>
            <input
              type="password"
              id="accessToken"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder="EAAAl..."
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              From Square Developer Dashboard → Applications → Credentials → Access Token
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setApplicationId('');
                setAccessToken('');
                setError('');
              }}
              className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="flex gap-3">
          <button
            onClick={() => setShowForm(true)}
            className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            {squareConfig?.configured ? 'Update Configuration' : 'Configure Square'}
          </button>
          {squareConfig?.configured && (
            <button
              onClick={handleClear}
              disabled={saving}
              className="py-2 px-4 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-600 bg-white hover:bg-red-50 disabled:opacity-50"
            >
              Clear Configuration
            </button>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">About Square Integration</h4>
        <p className="text-sm text-blue-800 mb-2">
          Square is used as a payment gateway only. BillingByScott handles all subscription logic internally:
        </p>
        <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
          <li>Customer management and card storage (PCI compliant)</li>
          <li>Payment processing for invoices</li>
          <li>No product catalog needed in Square</li>
        </ul>
        <p className="text-sm text-blue-800 mt-2">
          Get your credentials from the{' '}
          <a
            href="https://developer.squareup.com/apps"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-blue-900"
          >
            Square Developer Dashboard
          </a>.
        </p>
      </div>
    </div>
  );
}
